import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Buildings, ArrowLeft, CheckCircle, SpinnerGap, Warning } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-hub-623/artifacts/04kw0jh0_IMG_4683.jpeg';

export const CorporatePage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    contact_email: '',
    contact_phone: '',
    number_of_users: '',
    billing_method: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/api/corporate-enquiry`, formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center space-x-2">
              <img src={LOGO_URL} alt="Vantage" className="h-12 w-auto" />
              <span className="text-xl font-bold tracking-tight">Vantage Fleet</span>
            </Link>
          </div>
          <div className="border border-border bg-background p-8">
            <CheckCircle weight="duotone" className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold tracking-tight mb-2">Enquiry Submitted</h1>
            <p className="text-muted-foreground mb-6">
              Thank you for your corporate enquiry. Our team will be in touch within 48 hours at the email address provided.
            </p>
            <Link to="/">
              <Button className="rounded-sm" data-testid="back-home-btn">Back to Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img src={LOGO_URL} alt="Vantage" className="h-12 w-auto" />
            <span className="text-xl font-bold tracking-tight">Vantage Fleet</span>
          </Link>
        </div>

        {/* Form Card */}
        <div className="border border-border bg-background">
          <div className="p-8 border-b border-border">
            <div className="flex items-center gap-3 mb-2">
              <Buildings weight="duotone" className="h-8 w-8 text-primary" />
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Corporate Accounts</h1>
            </div>
            <p className="text-muted-foreground">
              Request multi-user access for your organisation. Complete the form below and our team will prepare a bespoke quote.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="corporate-error">
                <Warning weight="bold" className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                placeholder="e.g. ABC Transport Ltd"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                required
                className="rounded-sm"
                data-testid="company-name-input"
              />
            </div>

            {/* Company Address */}
            <div className="space-y-2">
              <Label htmlFor="company_address">Company Address *</Label>
              <Textarea
                id="company_address"
                placeholder="Full registered address"
                value={formData.company_address}
                onChange={(e) => handleChange('company_address', e.target.value)}
                required
                rows={3}
                className="rounded-sm"
                data-testid="company-address-input"
              />
            </div>

            {/* Contact Email & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  placeholder="accounts@company.com"
                  value={formData.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  required
                  className="rounded-sm"
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone *</Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  placeholder="01onal 234 5678"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  required
                  className="rounded-sm"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>

            {/* Number of Users & Billing */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="number_of_users">Number of Users Required *</Label>
                <Input
                  id="number_of_users"
                  type="number"
                  min="2"
                  placeholder="e.g. 10"
                  value={formData.number_of_users}
                  onChange={(e) => handleChange('number_of_users', e.target.value)}
                  required
                  className="rounded-sm"
                  data-testid="num-users-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Billing Preference *</Label>
                <Select 
                  value={formData.billing_method} 
                  onValueChange={(v) => handleChange('billing_method', v)}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="billing-select">
                    <SelectValue placeholder="Select billing method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre_pay">Pre Pay</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="direct_debit">Direct Debit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full rounded-sm h-12 text-base"
              disabled={loading || !formData.billing_method}
              data-testid="submit-enquiry-btn"
            >
              {loading ? (
                <><SpinnerGap className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>
              ) : (
                'Submit Corporate Enquiry'
              )}
            </Button>
          </form>
        </div>

        {/* Back */}
        <div className="mt-6 text-center">
          <Link to="/">
            <Button variant="ghost" className="rounded-sm" data-testid="back-home-link">
              <ArrowLeft weight="bold" className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
